import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, ArrowRight, Clock, TrendingUp, ChevronRight, Loader2 } from 'lucide-react';
import { loadCatalog, itemPrice, KIND_LABELS } from '../../search/catalog';
import { suggest, POPULAR_SEARCHES } from '../../search/engine';
import { fold } from '../../search/text';

// The store search: opens from the header (desktop field, mobile icon or the "/" key), suggests
// products, categories and collections while typing, and sends the query to /tienda.
const RECENT_KEY = 'proomtb-recent-searches';

export const shopUrl = (query) => `/tienda?q=${encodeURIComponent(query)}`;

function readRecent() {
    try {
        const list = JSON.parse(localStorage.getItem(RECENT_KEY));
        return Array.isArray(list) ? list : [];
    } catch (e) {
        return [];
    }
}

function saveRecent(query) {
    try {
        const list = [query, ...readRecent().filter((q) => fold(q) !== fold(query))].slice(0, 6);
        localStorage.setItem(RECENT_KEY, JSON.stringify(list));
    } catch (e) { /* private mode: nothing to remember */ }
}

function clearRecent() {
    try { localStorage.removeItem(RECENT_KEY); } catch (e) { /* nothing stored */ }
}

// Google Analytics "search" event (gtag.js is loaded in public/index.html)
function track(query) {
    if (typeof window.gtag === 'function') window.gtag('event', 'search', { search_term: query });
}

// Bolds the words of a name that the query matches
const Highlight = ({ text, tokens }) => text.split(/(\s+)/).map((part, i) => {
    const word = fold(part);
    const hit = word && tokens.some((t) => word.startsWith(t));
    return hit ? <mark key={i} className="bg-transparent text-black font-black">{part}</mark> : <React.Fragment key={i}>{part}</React.Fragment>;
});

const QUICK_LINKS = [
    { label: 'E-Bikes', url: '/ebikes' },
    { label: 'Bicicletas Raymon', url: '/catalogo' },
    { label: 'Bicis de Navidad', url: '/navidad' },
    { label: 'Accesorios', url: '/accesorios' }
];

const Chip = ({ icon: Icon, children, onClick }) => (
    <button type="button" onClick={onClick} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border border-gray-200 text-sm text-gray-700 hover:border-black hover:text-black transition-colors">
        {Icon && <Icon size={14} className="text-gray-400" />}
        {children}
    </button>
);

const SearchPanel = ({ open, onClose }) => {
    const navigate = useNavigate();
    const inputRef = useRef(null);
    const [query, setQuery] = useState('');
    const [debounced, setDebounced] = useState('');
    const [items, setItems] = useState(null);
    const [failed, setFailed] = useState(false);
    const [active, setActive] = useState(-1);
    const [recent, setRecent] = useState([]);

    useEffect(() => {
        if (!open) return undefined;
        setQuery('');
        setDebounced('');
        setActive(-1);
        setRecent(readRecent());
        loadCatalog().then(setItems).catch(() => setFailed(true));
        const focus = setTimeout(() => inputRef.current && inputRef.current.focus(), 30);
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        const overflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            clearTimeout(focus);
            window.removeEventListener('keydown', onKey);
            document.body.style.overflow = overflow;
        };
    }, [open, onClose]);

    useEffect(() => {
        const t = setTimeout(() => setDebounced(query), 120);
        return () => clearTimeout(t);
    }, [query]);

    const term = debounced.trim();
    const result = useMemo(() => (items && term ? suggest(items, term) : null), [items, term]);
    const tokens = useMemo(() => (term ? fold(term).split(/[^a-z0-9.]+/).filter(Boolean) : []), [term]);

    // Everything the arrow keys can land on, in the order it is drawn
    const rows = useMemo(() => {
        if (!result) return [];
        return [
            ...result.links.map((l) => ({ url: l.url })),
            ...result.products.map((p) => ({ url: p.url })),
            ...(result.total ? [{ url: shopUrl(term) }] : [])
        ];
    }, [result, term]);

    useEffect(() => { setActive(-1); }, [rows]);

    const go = (url, remember) => {
        if (remember) saveRecent(remember);
        onClose();
        navigate(url);
    };

    const submit = (e) => {
        e.preventDefault();
        const q = query.trim();
        if (active >= 0 && rows[active]) return go(rows[active].url, q);
        if (!q) return undefined;
        track(q);
        return go(shopUrl(q), q);
    };

    const onKeyDown = (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActive((a) => Math.min(a + 1, rows.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActive((a) => Math.max(a - 1, -1));
        }
    };

    if (!open) return null;

    let index = -1;
    const rowClass = (i) => `w-full flex items-center gap-4 px-3 py-2.5 rounded-2xl text-left transition-colors ${active === i ? 'bg-gray-100' : 'hover:bg-gray-50'}`;

    return (
        <div className="fixed inset-0 z-[100]" role="dialog" aria-modal="true" aria-label="Buscar en la tienda">
            <div className="absolute inset-0 bg-black/40" onClick={onClose} />
            <div className="absolute inset-x-0 top-0 h-full lg:h-auto lg:max-h-[88vh] bg-white shadow-2xl lg:rounded-b-[2rem] flex flex-col">
                <form onSubmit={submit} role="search" className="w-full max-w-4xl mx-auto px-4 sm:px-6 pt-4 lg:pt-6">
                    <div className="relative flex items-center">
                        <Search className="absolute left-5 text-gray-400 pointer-events-none" size={22} />
                        <input
                            ref={inputRef}
                            type="search"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            onKeyDown={onKeyDown}
                            placeholder="Buscar bicicletas, e-bikes, accesorios…"
                            aria-label="Buscar productos"
                            autoFocus
                            autoComplete="off"
                            autoCorrect="off"
                            spellCheck={false}
                            enterKeyHint="search"
                            role="combobox"
                            aria-autocomplete="list"
                            aria-controls="search-suggestions"
                            aria-expanded={rows.length > 0}
                            aria-activedescendant={active >= 0 ? `search-option-${active}` : undefined}
                            className="w-full h-14 pl-14 pr-28 rounded-full bg-gray-100 text-base sm:text-lg font-medium text-black placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-black [&::-webkit-search-cancel-button]:hidden"
                        />
                        {query && (
                            <button type="button" onClick={() => { setQuery(''); inputRef.current && inputRef.current.focus(); }} aria-label="Borrar búsqueda" className="absolute right-16 p-2 text-gray-400 hover:text-black">
                                <X size={18} />
                            </button>
                        )}
                        <button type="button" onClick={onClose} aria-label="Cerrar búsqueda" className="absolute right-2 h-10 w-10 rounded-full flex items-center justify-center text-gray-500 hover:bg-gray-200 hover:text-black">
                            <X size={22} />
                        </button>
                    </div>
                </form>

                <div className="flex-1 overflow-y-auto overscroll-contain">
                    <div id="search-suggestions" role="listbox" className="w-full max-w-4xl mx-auto px-4 sm:px-6 py-6">
                        {!term && (
                            <div className="space-y-8">
                                {recent.length > 0 && (
                                    <section>
                                        <div className="flex items-center justify-between mb-3">
                                            <h2 className="text-xs font-black uppercase tracking-widest text-gray-400">Búsquedas recientes</h2>
                                            <button type="button" onClick={() => { clearRecent(); setRecent([]); }} className="text-xs font-bold text-gray-400 hover:text-black">Borrar</button>
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                            {recent.map((q) => <Chip key={q} icon={Clock} onClick={() => go(shopUrl(q), q)}>{q}</Chip>)}
                                        </div>
                                    </section>
                                )}
                                <section>
                                    <h2 className="text-xs font-black uppercase tracking-widest text-gray-400 mb-3">Búsquedas populares</h2>
                                    <div className="flex flex-wrap gap-2">
                                        {POPULAR_SEARCHES.map((q) => <Chip key={q} icon={TrendingUp} onClick={() => { track(q); go(shopUrl(q), q); }}>{q}</Chip>)}
                                    </div>
                                </section>
                                <section>
                                    <h2 className="text-xs font-black uppercase tracking-widest text-gray-400 mb-3">Explorar</h2>
                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                        {QUICK_LINKS.map((l) => (
                                            <button key={l.url} type="button" onClick={() => go(l.url)} className="flex items-center justify-between px-4 py-3.5 rounded-2xl bg-gray-50 hover:bg-gray-100 text-sm font-bold text-black text-left">
                                                {l.label} <ChevronRight size={16} className="text-gray-400" />
                                            </button>
                                        ))}
                                    </div>
                                </section>
                            </div>
                        )}

                        {term && !result && !failed && (
                            <div className="flex items-center gap-3 text-sm text-gray-500 py-6"><Loader2 size={18} className="animate-spin" /> Buscando…</div>
                        )}

                        {term && failed && (
                            <div className="py-6 text-sm text-gray-500">
                                No pudimos cargar el catálogo. <button type="button" onClick={() => go(shopUrl(term), term)} className="font-bold text-black underline">Buscar en la tienda</button>
                            </div>
                        )}

                        {result && (
                            <div className="space-y-6">
                                {result.links.length > 0 && (
                                    <section>
                                        <h2 className="text-xs font-black uppercase tracking-widest text-gray-400 mb-2 px-3">Categorías y colecciones</h2>
                                        <ul>
                                            {result.links.map((l) => {
                                                index += 1;
                                                const i = index;
                                                return (
                                                    <li key={l.url} id={`search-option-${i}`} role="option" aria-selected={active === i}>
                                                        <button type="button" onMouseEnter={() => setActive(i)} onClick={() => go(l.url, term)} className={rowClass(i)}>
                                                            <span className="w-9 h-9 shrink-0 rounded-full bg-gray-100 flex items-center justify-center text-gray-500"><ArrowRight size={16} /></span>
                                                            <span className="text-sm font-bold text-black">{l.name}</span>
                                                        </button>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </section>
                                )}

                                {result.products.length > 0 && (
                                    <section>
                                        <h2 className="text-xs font-black uppercase tracking-widest text-gray-400 mb-2 px-3">Productos</h2>
                                        <ul>
                                            {result.products.map((p) => {
                                                index += 1;
                                                const i = index;
                                                return (
                                                    <li key={p.key} id={`search-option-${i}`} role="option" aria-selected={active === i}>
                                                        <button type="button" onMouseEnter={() => setActive(i)} onClick={() => go(p.url, term)} className={rowClass(i)}>
                                                            <span className="w-14 h-14 shrink-0 rounded-xl bg-white border border-gray-100 flex items-center justify-center overflow-hidden">
                                                                {p.image ? <img src={p.image} alt="" className="max-w-full max-h-full object-contain mix-blend-multiply" loading="lazy" referrerPolicy="no-referrer" /> : <Search size={16} className="text-gray-300" />}
                                                            </span>
                                                            <span className="min-w-0 flex-1">
                                                                <span className="block text-sm font-bold text-black truncate"><Highlight text={p.name} tokens={tokens} /></span>
                                                                <span className="block text-xs text-gray-500 truncate">{KIND_LABELS[p.kind]}{p.subtitle ? ` · ${p.subtitle}` : ''}</span>
                                                            </span>
                                                            <span className="text-sm font-bold text-black whitespace-nowrap">{itemPrice(p)}</span>
                                                        </button>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </section>
                                )}

                                {result.total > 0 ? (() => {
                                    index += 1;
                                    const i = index;
                                    return (
                                        <div id={`search-option-${i}`} role="option" aria-selected={active === i} className="pt-2">
                                            <button type="button" onMouseEnter={() => setActive(i)} onClick={() => { track(term); go(shopUrl(term), term); }} className={`w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full text-sm font-black uppercase tracking-wider transition-colors ${active === i ? 'bg-gray-800 text-white' : 'bg-black text-white hover:bg-gray-800'}`}>
                                                Ver {result.total === 1 ? 'el único resultado' : `los ${result.total} resultados`} <ArrowRight size={16} />
                                            </button>
                                        </div>
                                    );
                                })() : (
                                    <div className="py-4">
                                        <p className="text-base font-bold text-black">No encontramos nada para “{term}”.</p>
                                        <p className="text-sm text-gray-500 mt-1 mb-4">Revisa la ortografía o prueba con una de estas búsquedas:</p>
                                        <div className="flex flex-wrap gap-2">
                                            {POPULAR_SEARCHES.map((q) => <Chip key={q} icon={TrendingUp} onClick={() => go(shopUrl(q), q)}>{q}</Chip>)}
                                        </div>
                                        <button type="button" onClick={() => go(shopUrl(term), term)} className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-black underline underline-offset-4">
                                            Buscar “{term}” en toda la tienda <ArrowRight size={14} />
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SearchPanel;
